import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../api/client';
import { Clock, Calendar, Phone, AlertCircle, CheckCircle } from 'lucide-react';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const OnCallStatusWidget = () => {
  const { user } = useAuth();
  const [onCallData, setOnCallData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOnCallStatus();
    // Refresh every 5 minutes
    const interval = setInterval(loadOnCallStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const loadOnCallStatus = async () => {
    try {
      const res = await api.get('/escalation/my-schedule');
      if (res.data?.success) {
        setOnCallData(res.data.data);
      }
    } catch (err) {
      console.error('Error loading on-call status:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-50 rounded-xl p-4 animate-pulse">
        <div className="h-4 bg-slate-200 rounded w-1/3 mb-2"></div>
        <div className="h-3 bg-slate-200 rounded w-1/2"></div>
      </div>
    );
  }

  // Not configured as on-call contact
  if (!onCallData?.isOnCallContact) {
    return null; // Don't show widget if not on-call roster
  }

  const { isCurrentlyOnCall, schedule, escalationLevels } = onCallData;

  return (
    <div className={`rounded-xl p-4 mb-6 ${
      isCurrentlyOnCall 
        ? 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200' 
        : 'bg-slate-50 border border-slate-200'
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isCurrentlyOnCall ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-500'
          }`}>
            {isCurrentlyOnCall ? <AlertCircle className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
          </div>
          <div>
            <h4 className="font-bold text-sm text-slate-900">
              {isCurrentlyOnCall ? 'You Are On-Call Now' : 'On-Call Status'}
            </h4>
            <p className="text-xs text-slate-500">
              {isCurrentlyOnCall 
                ? 'Responsible for emergency escalations'
                : getNextOnCallText(schedule)
              }
            </p>
          </div>
        </div>
        
        {isCurrentlyOnCall && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-bold">
            <Phone className="w-3.5 h-3.5" />
            Active
          </div>
        )}
      </div>

      {/* Schedule Preview */}
      {!isCurrentlyOnCall && schedule?.shifts?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-200/60">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <Calendar className="w-4 h-4" />
            <span className="font-medium">Your upcoming shifts:</span>
          </div>
          <div className="mt-2 space-y-1">
            {schedule.shifts.slice(0, 3).map((shift, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs text-slate-500">
                <span className="font-medium text-slate-700">{DAYS[shift.dayOfWeek]}</span>
                <span>{shift.startTime} - {shift.endTime}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {schedule?.isAlwaysOnCall && (
        <div className="mt-3 pt-3 border-t border-slate-200/60">
          <div className="flex items-center gap-2 text-xs text-emerald-600">
            <CheckCircle className="w-4 h-4" />
            <span className="font-medium">24/7 Primary On-Call Coverage</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper to show next on-call time
function getNextOnCallText(schedule) {
  if (schedule?.isAlwaysOnCall) return 'Always on-call';
  if (!schedule?.shifts?.length) return 'No shifts scheduled';
  
  const now = new Date();
  const currentDay = now.getDay();
  const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  
  // Find next shift
  let nextShift = null;
  let daysUntil = 7;
  
  for (let i = 0; i < 7; i++) {
    const checkDay = (currentDay + i) % 7;
    const shift = schedule.shifts.find(s => s.dayOfWeek === checkDay);
    
    if (shift) {
      if (i === 0 && shift.startTime > currentTime) {
        nextShift = shift;
        daysUntil = 0;
        break;
      } else if (i > 0) {
        nextShift = shift;
        daysUntil = i;
        break;
      }
    }
  }
  
  if (!nextShift) return 'No upcoming shifts';
  
  if (daysUntil === 0) return `Today at ${nextShift.startTime}`;
  if (daysUntil === 1) return `Tomorrow at ${nextShift.startTime}`;
  return `${DAYS[nextShift.dayOfWeek]} at ${nextShift.startTime}`;
}

export default OnCallStatusWidget;
